import * as tf from '@tensorflow/tfjs';
import * as tmPose from '@teachablemachine/pose';

import * as build_part_with_score_queue_1 from "@tensorflow-models/posenet/dist/multi_pose/build_part_with_score_queue"
import * as decode_pose_1 from "@tensorflow-models/posenet/dist/multi_pose/decode_pose"
import * as util_1 from "@tensorflow-models/posenet/dist/multi_pose/util"
import * as util from "@tensorflow-models/posenet/dist/util"

const MAX_PREDICTIONS = 3;
var kLocalMaximumRadius = 1;

function getInputTensorDimensions(input) {
    return input instanceof tf.Tensor ? [input.shape[0], input.shape[1]] :
        [input.height, input.width];
}

function toInputTensor(input) {
    return input instanceof tf.Tensor ? input : tf.browser.fromPixels(input);
}

function padAndResizeTo(input, _a){
    var targetH = _a[0], targetW = _a[1];
    var _b = getInputTensorDimensions(input), height = _b[0], width = _b[1];
    var targetAspect = targetW / targetH;
    var aspect = width / height;
    var _c = [0, 0, 0, 0], padT = _c[0], padB = _c[1], padL = _c[2], padR = _c[3];
    if (aspect < targetAspect) {
        // pads the width
        padT = 0;
        padB = 0;
        padL = Math.round(0.5 * (targetAspect * height - width));
        padR = Math.round(0.5 * (targetAspect * height - width));
    }
    else {
        // pads the height
        padT = Math.round(0.5 * ((1.0 / targetAspect) * width - height));
        padB = Math.round(0.5 * ((1.0 / targetAspect) * width - height));
        padL = 0;
        padR = 0;
    }

    const imageTensor = toInputTensor(input);
    const imageTensorPad = tf.pad3d(imageTensor, [[padT, padB], [padL, padR], [0, 0]]);
    const resized = tf.image.resizeBilinear(imageTensorPad, [targetH, targetW])
    imageTensor.dispose()
    imageTensorPad.dispose()

    return { resized: resized, padding: { top: padT, left: padL, right: padR, bottom: padB } };
}

function nameOutputResults (results) {
    var offsets = results[0], heatmap = results[1], displacementFwd = results[2], displacementBwd = results[3];
    return { offsets: offsets, heatmap: heatmap, displacementFwd: displacementFwd, displacementBwd: displacementBwd };
}

function poseOutputsToAray (heatmapScores, offsets, displacementFwd, displacementBwd) {
    const axis = 2;
    const array = [heatmapScores, offsets]
    const concat = tf.concat(array, axis);
    const concatArray = concat.dataSync();

    concat.dispose();
    
    return concatArray;
}

function scalePose(pose, scaleY, scaleX, offsetY, offsetX) {
    if (offsetY === void 0) { offsetY = 0; }
    if (offsetX === void 0) { offsetX = 0; }
    return {
        score: pose.score,
        keypoints: pose.keypoints.map(function (_a) {
            var score = _a.score, part = _a.part, position = _a.position;
            return ({
                score: score,
                part: part,
                position: {
                    x: position.x * scaleX + offsetX,
                    y: position.y * scaleY + offsetY
                }
            });
        })
    };
}

function scalePoses(poses, scaleY, scaleX, offsetY, offsetX) {
    if (offsetY === void 0) { offsetY = 0; }
    if (offsetX === void 0) { offsetX = 0; }
    if (scaleX === 1 && scaleY === 1 && offsetY === 0 && offsetX === 0) {
        return poses;
    }
    return poses.map(function (pose) { return scalePose(pose, scaleY, scaleX, offsetY, offsetX); });
}

function flipPoseHorizontal(pose, imageWidth) {
    return {
        score: pose.score,
        keypoints: pose.keypoints.map(function (_a) {
            var score = _a.score, part = _a.part, position = _a.position;
            return ({
                score: score,
                part: part,
                position: { x: imageWidth - 1 - position.x, y: position.y }
            });
        })
    };
}

function flipPosesHorizontal(poses, imageWidth) {
    if (imageWidth <= 0) {
        return poses;
    }
    return poses.map(function (pose) { return flipPoseHorizontal(pose, imageWidth); });
}

function scaleAndFlipPoses(poses, _a, _b, padding, flipHorizontal) {
    var height = _a[0], width = _a[1];
    var inputResolutionHeight = _b[0], inputResolutionWidth = _b[1];
    var scaleY = (height + padding.top + padding.bottom) / (inputResolutionHeight);
    var scaleX = (width + padding.left + padding.right) / (inputResolutionWidth);
    var scaledPoses = scalePoses(poses, scaleY, scaleX, -padding.top, -padding.left);
    if (flipHorizontal) {
        return flipPosesHorizontal(scaledPoses, width);
    }
    else {
        return scaledPoses;
    }
}

async function poseOutputsToKeypoints(
    model,
    input,
    heatmapScores,
    offsets,
    displacementFwd,
    displacementBwd,
    padding,
    flipHorizontal
) {
    const config = {
        maxDetections: MAX_PREDICTIONS,
        scoreThreshold: 0.5,
        nmsRadius: 20
    };

    const [height, width] = getInputTensorDimensions(input);

    const outputStride = model.posenetModel.baseModel.outputStride;
    const inputResolution = model.posenetModel.inputResolution;

    const [scoresBuffer, offsetsBuffer, displacementsFwdBuffer, displacementsBwdBuffer] 
        = await util.toTensorBuffers3D([heatmapScores, offsets, displacementFwd, displacementBwd]);

    const poses = decodeMultiplePoses(scoresBuffer, offsetsBuffer, displacementsFwdBuffer,
        displacementsBwdBuffer, outputStride, config.maxDetections, config.scoreThreshold, config.nmsRadius);

    const resultPoses = scaleAndFlipPoses(poses, [height, width], inputResolution, padding, flipHorizontal);

    return resultPoses[0];
}

const baseModelPredict = function (model, input) {
    function preprocessInput(input) {
        const divTensor = tf.div(input, 127.5)
        const subTensor = tf.sub(divTensor, 1.0)
        divTensor.dispose()
        return subTensor
    }
    const castTensor = tf.cast(input, 'float32')
    var asFloat = preprocessInput(castTensor);
    castTensor.dispose()

    var asBatch = tf.expandDims(asFloat, 0);
    var results = model.posenetModel.baseModel.model.predict(asBatch);
    var results3d = results.map(function (y) { return tf.squeeze(y, [0]); });
    var namedResults = nameOutputResults(results3d);
    var heatmapScores = tf.sigmoid(namedResults.heatmap)

    
    asFloat.dispose()
    asBatch.dispose()
    for (let result of results) {
        result.dispose()
    }
    namedResults.heatmap.dispose()
    
    return {
        heatmapScores: heatmapScores,
        offsets: namedResults.offsets,
        displacementFwd: namedResults.displacementFwd,
        displacementBwd: namedResults.displacementBwd,
        results3d,
        namedResults
    };
};

function withinNmsRadiusOfCorrespondingPoint(poses, squaredNmsRadius, _a, keypointId) {
    var x = _a.x, y = _a.y;
    return poses.some(function (_a) {
        var keypoints = _a.keypoints;
        var correspondingKeypoint = keypoints[keypointId].position;
        return util_1.squaredDistance(y, x, correspondingKeypoint.y, correspondingKeypoint.x) <=
            squaredNmsRadius;
    });
}

function getInstanceScore(existingPoses, squaredNmsRadius, instanceKeypoints) {
    var notOverlappedKeypointScores = instanceKeypoints.reduce(function (result, _a, keypointId) {
        var position = _a.position, score = _a.score;
        if (!withinNmsRadiusOfCorrespondingPoint(existingPoses, squaredNmsRadius, position, keypointId)) {
            result += score;
        }
        return result;
    }, 0.0);
    return notOverlappedKeypointScores /= instanceKeypoints.length;
}

function decodeMultiplePoses(scoresBuffer, offsetsBuffer, displacementsFwdBuffer, displacementsBwdBuffer, outputStride, maxPoseDetections, scoreThreshold, nmsRadius){
    if (scoreThreshold === void 0) { scoreThreshold = 0.5; }
    if (nmsRadius === void 0) { nmsRadius = 20; }
    var poses = [];
    var queue = build_part_with_score_queue_1.buildPartWithScoreQueue(scoreThreshold, kLocalMaximumRadius, scoresBuffer);
    var squaredNmsRadius = nmsRadius * nmsRadius;
    // Generate at most maxDetections object instances per image in
    // decreasing root part score order.
    while (poses.length < maxPoseDetections && !queue.empty()) {
        // The top element in the queue is the next root candidate.
        var root = queue.dequeue();
        // Part-based non-maximum suppression: We reject a root candidate if it
        // is within a disk of `nmsRadius` pixels from the corresponding part of
        // a previously detected instance.
        var rootImageCoords = util_1.getImageCoords(root.part, outputStride, offsetsBuffer);
        if (withinNmsRadiusOfCorrespondingPoint(poses, squaredNmsRadius, rootImageCoords, root.part.id)) {
            continue;
        }
        // Start a new detection instance at the position of the root.
        var keypoints = decode_pose_1.decodePose(root, scoresBuffer, offsetsBuffer, outputStride, displacementsFwdBuffer, displacementsBwdBuffer);
        var score = getInstanceScore(poses, squaredNmsRadius, keypoints);
        poses.push({ keypoints: keypoints, score: score });
    }
    return poses;
}

async function _estimatePose(model, sample, flipHorizontal = false){
    const inputResolution = model.posenetModel.inputResolution;
    const { resized, padding } = padAndResizeTo(sample, inputResolution);
    const {heatmapScores, offsets, displacementFwd, displacementBwd, results3d, namedResults} = baseModelPredict(model, resized);
    
    const posenetOutput = poseOutputsToAray(heatmapScores, offsets, displacementFwd, displacementBwd)

    const pose  = await poseOutputsToKeypoints(
        model,
        sample,
        heatmapScores,
        offsets,
        displacementFwd,
        displacementBwd,
        padding,
        flipHorizontal
    );

    heatmapScores.dispose();
    offsets.dispose();
    displacementFwd.dispose();
    displacementBwd.dispose();
    resized.dispose();

    return {pose, posenetOutput}
}

function drawKeypoints(...args){
    return tmPose.drawKeypoints(...args)
}

function drawSkeleton(...args){
    return tmPose.drawSkeleton(...args)
}

export async function createPoseModel(){
    let model = await tmPose.createTeachable({   
        tfjsVersion: tf.version.tfjs, 
        tmVersion: tmPose.version,
        /* modelSettings: {
            posenet: {}
        }, */
        packageVersion: '0.8.6',
        packageName: '@teachablemachine/pose',
        timeStamp: new Date().toISOString(),
        userMetadata: {},
        modelName: "untitled",
        labels: [],
    })
    model.posenetModel.baseModel.model = await tf.loadGraphModel('https://models.eduairsz.xyz/posenet/mobilenet-float-075-model-stride16.json')
    model.setSeed("testSuite")

    return model
}

export const model = createPoseModel();

export async function estimateAndDraw(image, canvas = document.createElement('canvas')){
    const result = await _estimatePose(await model, image)
    if (!result) {
        return {canvas: null, pose: null, posenetOutput: null};
    }

    if(canvas) {
        canvas.width = 224; canvas.height = 224;
        const ctx = canvas.getContext('2d')
        ctx.drawImage(image, 0, 0)
    
        if(result.pose){
            const minPartConfidence = 0.5;
            drawKeypoints(result.pose.keypoints, minPartConfidence, ctx);
            drawSkeleton(result.pose.keypoints, minPartConfidence, ctx);
        }
    }

    return {canvas: canvas, pose: result.pose, posenetOutput: result.posenetOutput}
}